// Does this record refer to that place? The one rule, in one file.
//
// A waypoint refers to a gazetteer row two ways -- `placeId`, the durable
// binding, and a name that normalizes to the row's `nameKey`, the bootstrap
// that binds routes written before the binding existed. That rule already
// existed inside scripts/merge-places.mjs, whose whole job is finding every
// reference to a row before deleting it, and whose header says the two must
// never disagree. It lives here now so they can't: the script asks the same
// question for a destructive reason, the /me/places/:id/visits page asks it
// for a read-only one, and both call the same predicate.
//
// WHAT DOESN'T LIVE HERE: what to do when a name key is CONTESTED -- when more
// than one row holds it. The two callers want genuinely different answers and
// neither is wrong. merge-places declines those waypoints, because after it
// deletes the loser they bind to the survivor on their own and forcing them
// onto the winner would move points the merge was never about. The page claims
// them for whichever row the app would actually resolve them to, because that
// is the row whose position the map is drawing. So the policy arrives as
// `ownerOfNameKey`, one line at each call site, next to the reasoning.
//
// Pure: no aws-amplify, no I/O. Same split, same reason, as PlaceIO.mjs.

import {
  ENTRY_ROUTE_KEY,
  TRIP_ROUTE_KEY,
  backfillEndpointNames,
  hasPlacedWaypoints,
  isPlaced,
  parseTripExtraFields,
  resolveRouteAgainstPlaces,
  routeFromFields,
} from './TripRoute.mjs';
import { normalizePlaceName } from './PlaceFields.mjs';

// How a waypoint reaches a place row.
export const PLACE_REF_ID = 'id';
export const PLACE_REF_NAME = 'name';
// A name match the caller's policy declines. Distinct from "no match" on
// purpose: merge-places reports these so a human can see what it left alone.
export const PLACE_REF_NAME_CONTESTED = 'name?';

// The place index (`loadPlaceIndex`'s { byId, byKey }) as a name-key policy:
// a name belongs to whichever row `pickPlace` already elected for it. This is
// the app's answer -- the row whose position every waypoint with that name is
// drawn at -- and so it is the page's.
export function nameKeyOwnerFrom(placeIndex) {
  const byKey = placeIndex?.byKey;
  if (!(byKey instanceof Map)) return () => null;
  return (nameKey) => byKey.get(nameKey)?.id ?? null;
}

// One waypoint's reference to one place, or null.
//
// The mechanics are repointWaypoint's, unchanged: the id binding, or a name
// bootstrap that only applies to a point with a position and a name of its own
// (the two cases resolveWaypoint refuses to overlay -- unplaced, and a name
// that IS the coordinates -- can't be claimed by a name).
export function waypointPlaceRef(waypoint, place, ownerOfNameKey) {
  if (!waypoint || !place) return null;
  if (waypoint.placeId === place.id) return PLACE_REF_ID;
  if (waypoint.placeId) return null;
  if (!isPlaced(waypoint) || waypoint.autoName) return null;

  const key = normalizePlaceName(waypoint.name);
  if (!key || key !== place.nameKey) return null;

  const owner = ownerOfNameKey ? ownerOfNameKey(key) : place.id;
  return owner === place.id ? PLACE_REF_NAME : PLACE_REF_NAME_CONTESTED;
}

// Every waypoint of one parsed route that refers to `place`, in route order.
// `contested` is reported rather than claimed -- see the header.
export function routePlaceRefs(route, place, ownerOfNameKey) {
  const refs = [];
  const contested = [];
  for (const waypoint of route?.waypoints || []) {
    const via = waypointPlaceRef(waypoint, place, ownerOfNameKey);
    if (via === PLACE_REF_NAME_CONTESTED) contested.push(waypoint.id);
    else if (via) refs.push({ waypoint, via });
  }
  return { refs, contested };
}

// One record's whole relationship to one place, or null when it has none.
//
// THE MATCH IS MADE AGAINST THE RESOLVED ROUTE, and the resolved route comes
// back with it. Two reasons, and the second is why it's not merely tidier:
//
//   - Whatever answers "is this record here" must be the same object the map
//     then draws, or a page can list a record it can't draw a line for. Handing
//     the route back makes that structural instead of a convention.
//   - It changes an answer. A waypoint whose `placeId` DANGLES (its row was
//     deleted) but whose name resolves here is adopted by resolveWaypoint --
//     the dead id doesn't block the name fallback -- so it genuinely refers to
//     this place, and the raw-route rule would miss it.
//
// After the resolve the name arm is rarely the one that fires: it survives for
// an empty index (resolveRouteAgainstPlaces is a no-op on one, deliberately)
// and for a row whose own coordinates are invalid, which resolveWaypoint
// refuses to overlay.
//
// `endpoints` is the record's From/To pair -- columns on a trip, blob keys on
// an entry. A record can name a place there and carry no route at all, which
// is most of the history predating routes; those count, and the caller marks
// them as not drawn.
export function recordPlaceUsage(record, place, placeIndex, { routeKey, endpoints } = {}) {
  if (!record || !place) return null;

  const ownerOfNameKey = nameKeyOwnerFrom(placeIndex);
  const from = endpoints?.from;
  const to = endpoints?.to;

  const route = resolveRouteAgainstPlaces(
    backfillEndpointNames(routeFromFields(record.fields, routeKey), { from, to }),
    placeIndex
  );
  const { refs, contested } = routePlaceRefs(route, place, ownerOfNameKey);

  // A From/To whose own anchor waypoint already matched is the same reference
  // seen twice -- backfillEndpointNames copies that very value into the anchor.
  const matchedKinds = new Set(refs.map(({ waypoint }) => waypoint.kind));
  const endpointHits = [];
  for (const [kind, value] of [['from', from], ['to', to]]) {
    if (matchedKinds.has(kind)) continue;
    const key = normalizePlaceName(value);
    if (!key || key !== place.nameKey) continue;
    if (ownerOfNameKey(key) !== place.id) continue;
    endpointHits.push(kind);
  }

  if (refs.length === 0 && endpointHits.length === 0) return null;

  return {
    route,
    refs,
    endpoints: endpointHits,
    // "Is there a line to draw", not "does the match itself have coordinates":
    // a record that reaches this place only through its From/To name but has a
    // drawable route elsewhere is still the same voyage.
    onMap: hasPlacedWaypoints(route),
    contested,
  };
}

// The two record shapes. These exist for the reason routeFromTrip and
// routeFromEntry do: the "which key does this record use" decision is made
// once, here. NOTHING outside this module passes a routeKey for a
// CompletedTask -- a Pilot's Logbook entry already owns `fields.route` as the
// free-text "Filed Route", and reading it as a route would be nonsense.
export function tripPlaceUsage(trip, place, placeIndex) {
  return recordPlaceUsage(trip, place, placeIndex, {
    routeKey: TRIP_ROUTE_KEY,
    // A trip's From/To are columns.
    endpoints: { from: trip?.from, to: trip?.to },
  });
}

export function entryPlaceUsage(entry, place, placeIndex) {
  // An entry's From/To live inside the blob, beside its route.
  const blob = parseTripExtraFields(entry?.fields);
  return recordPlaceUsage(entry, place, placeIndex, {
    routeKey: ENTRY_ROUTE_KEY,
    endpoints: { from: blob.from, to: blob.to },
  });
}
